import React from 'react';
import { SystemService } from 'services';
import { Globals } from 'utils';
import { Modal, Pagination } from 'components';
import moment from 'moment';

// ============================================================
// Shapes locales — tipamos lo que la pantalla consume del backend.
// El SystemService ya expone interfaces compatibles, las re-declaramos
// acá con todos los campos opcionales para tener control fino del
// render sin asumir presencia de cada propiedad.
// ============================================================

interface SearchMatch {
	symbol?: string;
	name?: string;
	type?: string;
	region?: string;
	currency?: string;
	matchScore?: number | string;
}

// Match de búsqueda de cripto — shape muy distinto a stocks: el backend
// matchea por símbolo EXACTO y devuelve N candidatos con CMC id, rank,
// is_active y un flag ya_existe que sabemos del estado actual del catálogo.
interface CryptoSearchMatch {
	id?: number;
	name?: string;
	symbol?: string;
	slug?: string;
	rank?: number | null;
	is_active?: boolean | number;
	ya_existe?: boolean;
}

interface AddedRow {
	symbol?: string;
	name?: string;
	price?: number | string;
	changePercent?: number | string;
}

interface AddFailed {
	symbol?: string;
	error?: string;
}

interface AddResponse {
	agregados?: AddedRow[];
	duplicados?: (string | AddedRow)[];
	agregados_sin_precio?: (string | AddedRow)[];
	fallidos?: AddFailed[];
	counts?: Record<string, number | undefined>;
}

interface StaleRow {
	id?: number | string;
	symbol?: string;
	name?: string;
	paid_updated_at?: string | null;
	created_at?: string | null;
	price?: number | string | null;
}

interface StaleResponse {
	threshold_hours?: number;
	counts?: {
		pendientes?: number;
		muertas?: number;
	};
	pendientes?: StaleRow[];
	muertas?: StaleRow[];
}

type StaleTab = 'muertas' | 'pendientes';

// ---------- Catálogo ----------

type CatalogOrderBy = 'priority' | 'symbol' | 'name' | 'paid_updated_at';
type CatalogOrderDir = 'asc' | 'desc';

interface CatalogRow {
	id?: number | string;
	symbol?: string;
	name?: string;
	price?: number | string | null;
	percent?: number | string | null;
	priority?: number | null;
	paid_updated_at?: string | null;
	color?: string | null;
	usuarios_count?: number;
}

interface CatalogResponse {
	rows?: CatalogRow[];
	count?: number;   // total de páginas según spec
	total?: number;   // total de filas
	page?: number;
	per_page?: number;
}

interface CatalogUserRow {
	id?: number | string;
	name?: string;
	lastname?: string;
	email?: string;
}

interface CatalogUsersResponse {
	stock_id?: number | string;
	crypto_id?: number | string;
	count?: number;
	users?: CatalogUserRow[];
}

// ============================================================
// Asset tabs (Bolsa / Cripto)
//
// La pantalla expone DOS tabs sobre la misma estructura de 3 secciones
// (buscar+agregar, stale, catálogo). Los endpoints son espejos pero
// con diferencias relevantes (id key, shape de search/add, formato de
// price). Para no duplicar 600+ líneas usamos `getAssetConfig(active)`
// que devuelve servicios + claves + flags que cambian, y el resto del
// render lee desde ahí.
// ============================================================

type AssetKind = 'stock' | 'crypto';

interface AssetCopy {
	tabLabel: string;
	pageDesc: string;
	searchSectionTitle: string;
	searchSectionDesc: string;
	searchLabel: string;
	searchPlaceholder: string;
	staleSectionTitle: string;
	catalogSectionTitle: string;
	emptyDeadLabel: string;
	emptyPendingLabel: string;
}

const COPY_STOCK: AssetCopy = {
	tabLabel: 'Bolsa',
	pageDesc: 'Gestión del catálogo de acciones: búsqueda, alta y mantenimiento',
	searchSectionTitle: 'Buscar y agregar',
	searchSectionDesc: 'Busca un símbolo (mínimo 2 caracteres) y agrégalo. Los resultados de Estados Unidos (USD) se priorizan arriba.',
	searchLabel: 'Buscar símbolo',
	searchPlaceholder: 'Ej: AAPL, microsoft, tesla…',
	staleSectionTitle: 'Acciones que no se actualizan',
	catalogSectionTitle: 'Catálogo completo',
	emptyDeadLabel: 'No hay acciones muertas. 🟢',
	emptyPendingLabel: 'No hay acciones pendientes de primera cotización.',
};

const COPY_CRYPTO: AssetCopy = {
	tabLabel: 'Cripto',
	pageDesc: 'Gestión del catálogo de criptomonedas: búsqueda, alta y mantenimiento',
	searchSectionTitle: 'Buscar y agregar',
	searchSectionDesc: 'Busca por símbolo EXACTO (ej. BTC) y elige cuál agregar. Cada coincidencia es una crypto distinta de CoinMarketCap — usa el rank para identificar la real.',
	searchLabel: 'Símbolo exacto',
	searchPlaceholder: 'BTC',
	staleSectionTitle: 'Cryptos que no se actualizan',
	catalogSectionTitle: 'Catálogo completo',
	emptyDeadLabel: 'No hay cryptos muertas. 🟢',
	emptyPendingLabel: 'No hay cryptos pendientes de primera cotización.',
};

const COPY: Record<AssetKind, AssetCopy> = {
	stock: COPY_STOCK,
	crypto: COPY_CRYPTO,
};

const CATALOG_PER_PAGE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_CATALOG_PER_PAGE = 25;
const DEFAULT_CATALOG_ORDER_BY: CatalogOrderBy = 'priority';
const DEFAULT_CATALOG_ORDER_DIR: CatalogOrderDir = 'asc';

const ORDER_BY_OPTIONS: { value: CatalogOrderBy; label: string }[] = [
	{ value: 'priority',        label: 'Priority' },
	{ value: 'symbol',          label: 'Símbolo' },
	{ value: 'name',            label: 'Nombre' },
	{ value: 'paid_updated_at', label: 'Actualizado' },
];

const REFRESH_INTERVAL_MS = 30 * 1000;
const TICK_INTERVAL_MS = 1000;
const SEARCH_DEBOUNCE_MS = 500;
const MIN_SEARCH_LEN = 2;

// ============================================================
// Helpers puros
// ============================================================

const formatNumber = (v: any): string => {
	const n = Number(v);
	if (isNaN(n)) return '0';
	return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

// Para stocks: parseamos y formateamos a 2-4 decimales (precio bursátil
// estándar). Para cryptos: el backend ya manda el string formateado
// (precisión variable: "0.0000000001700704"), lo respetamos tal cual.
const formatStockPrice = (v: any): string => {
	if (v === null || v === undefined || v === '') return '—';
	const n = Number(v);
	if (!isFinite(n)) return '—';
	return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
}

const formatCryptoPrice = (v: any): string => {
	if (v === null || v === undefined || v === '') return '—';
	// String ya formateado → mostrar tal cual; si viniera number, casteo simple.
	if (typeof v === 'string') return v;
	const n = Number(v);
	if (!isFinite(n)) return '—';
	return String(n);
}

// Devuelve { text, cls } para el % de variación. cls = 'up' | 'down' | 'flat'
// y se mapea a colores en SCSS (verde para up, rojo para down, neutro flat).
const formatPercent = (v: any): { text: string; cls: 'up' | 'down' | 'flat' } => {
	if (v === null || v === undefined || v === '') return { text: '—', cls: 'flat' };
	const n = Number(v);
	if (!isFinite(n)) return { text: '—', cls: 'flat' };
	const sign = n > 0 ? '+' : '';
	const cls: 'up' | 'down' | 'flat' = n > 0 ? 'up' : (n < 0 ? 'down' : 'flat');
	return { text: `${ sign }${ n.toFixed(2) }%`, cls };
}

const buildFullName = (u: CatalogUserRow): string => {
	const parts = [u.name, u.lastname].filter(Boolean) as string[];
	return parts.join(' ') || (u.email || '—');
}

const humanizeAgo = (iso?: string | null, now: number = Date.now()): string => {
	if (!iso) return '—';
	const t = moment(iso);
	if (!t.isValid()) return '—';
	const diff = Math.floor((now - t.valueOf()) / 1000);
	if (diff < 0) return 'ahora';
	if (diff < 5) return 'hace instantes';
	if (diff < 60) return `hace ${ diff } seg`;
	const mins = Math.floor(diff / 60);
	if (mins < 60) return `hace ${ mins } min`;
	const hours = Math.floor(mins / 60);
	if (hours < 24) return `hace ${ hours }h`;
	const days = Math.floor(hours / 24);
	if (days < 30) return `hace ${ days }d`;
	const months = Math.floor(days / 30);
	if (months < 12) return `hace ${ months } mes${ months === 1 ? '' : 'es' }`;
	const years = Math.floor(months / 12);
	return `hace ${ years } año${ years === 1 ? '' : 's' }`;
}

// El backend devuelve resultados mezclados entre regiones; el equipo
// quiere ver US/USD primero porque es lo que se va a agregar en el
// 95% de los casos. Sort estable: clave compuesta {priority US, region, name}.
const isUS = (m: SearchMatch): boolean => {
	const region = (m.region || '').toLowerCase();
	const currency = (m.currency || '').toUpperCase();
	return region === 'united states' || region.startsWith('united states') || currency === 'USD';
}

const sortMatches = (matches: SearchMatch[]): SearchMatch[] => {
	const copy = [...matches];
	copy.sort((a, b) => {
		const aUS = isUS(a) ? 0 : 1;
		const bUS = isUS(b) ? 0 : 1;
		if (aUS !== bUS) return aUS - bUS;
		const regA = (a.region || '').localeCompare(b.region || '');
		if (regA !== 0) return regA;
		return (a.symbol || '').localeCompare(b.symbol || '');
	});
	return copy;
}

// Sort de matches de cripto: rank ascendente (1, 2, 3…), null al final
// (típicamente proyectos abandonados o de baja calidad).
const sortCryptoMatches = (matches: CryptoSearchMatch[]): CryptoSearchMatch[] => {
	const copy = [...matches];
	copy.sort((a, b) => {
		const aRank = (a.rank === null || a.rank === undefined) ? Number.POSITIVE_INFINITY : Number(a.rank);
		const bRank = (b.rank === null || b.rank === undefined) ? Number.POSITIVE_INFINITY : Number(b.rank);
		if (aRank !== bRank) return aRank - bRank;
		return (a.name || '').localeCompare(b.name || '');
	});
	return copy;
}

// Normaliza un item que el backend pueda mandar como string ("AAPL")
// o como objeto ({symbol:"AAPL"}). Útil para `duplicados` y
// `agregados_sin_precio` que tienen union type. Para cripto pueden
// venir como number (id) o como objeto.
const labelOfMaybeRow = (x: string | number | AddedRow | undefined): string => {
	if (x === undefined || x === null) return '';
	if (typeof x === 'string') return x;
	if (typeof x === 'number') return String(x);
	return x.symbol || x.name || '';
}

// ============================================================
// State — buckets independientes por asset
//
// Cada tab tiene su propio sub-estado completo (search/add, stale,
// catálogo) para que cambiar de tab NO pise datos del otro. Los
// modales (edit, users) sí son globales: solo hay uno visible a la
// vez en cualquier tab, y guardan su `assetKind` para saber a qué
// servicio pegarle al confirmar.
// ============================================================

interface AssetBucket {
	// SECCIÓN A — Buscar / Agregar
	// Para stocks: searchMatches = SearchMatch[] (texto libre).
	// Para cryptos: searchMatches = CryptoSearchMatch[] (símbolo exacto).
	// Tipo unión: el render hace branch por activeAsset.
	searchInput: string;
	searchApplied: string;
	searchLoading: boolean;
	searchError: string | null;
	searchMatches: SearchMatch[] | CryptoSearchMatch[];
	addingKey: Record<string, boolean>; // por símbolo (stock) o por id (crypto)

	// Bulk solo aplica a stocks. Para cryptos no se renderiza.
	bulkRaw: string;
	bulkSubmitting: boolean;

	lastAddResult: AddResponse | null;

	// SECCIÓN B — Stale (StaleResponse y CryptosStaleResponse comparten shape)
	staleData: StaleResponse | null;
	staleLoading: boolean;
	staleLoadError: boolean;
	staleTab: StaleTab;
	rowAction: Record<string, 'retry' | 'delete'>;

	// SECCIÓN C — Catálogo
	catalogData: CatalogResponse | null;
	catalogLoading: boolean;
	catalogLoadError: boolean;
	catalogPage: number;
	catalogPerPage: number;
	catalogOrderBy: CatalogOrderBy;
	catalogOrderDir: CatalogOrderDir;
	catalogSearch: string;
	catalogSearchApplied: string;
	catalogRowAction: Record<string, 'edit' | 'delete'>;

	// Edición inline del umbral de stale (threshold_hours) — por asset.
	// `thresholdEditOpen` controla si el control está expandido (modo input);
	// cuando está colapsado mostramos solo el badge "Umbral: Xh" con un
	// botón "Editar". Independiente entre stock y crypto.
	thresholdEditOpen: boolean;
	thresholdInput: string;
	thresholdSubmitting: boolean;
}

const makeEmptyBucket = (): AssetBucket => ({
	searchInput: '',
	searchApplied: '',
	searchLoading: false,
	searchError: null,
	searchMatches: [],
	addingKey: {},
	bulkRaw: '',
	bulkSubmitting: false,
	lastAddResult: null,
	staleData: null,
	staleLoading: true,
	staleLoadError: false,
	staleTab: 'muertas',
	rowAction: {},
	catalogData: null,
	catalogLoading: true,
	catalogLoadError: false,
	catalogPage: 1,
	catalogPerPage: DEFAULT_CATALOG_PER_PAGE,
	catalogOrderBy: DEFAULT_CATALOG_ORDER_BY,
	catalogOrderDir: DEFAULT_CATALOG_ORDER_DIR,
	catalogSearch: '',
	catalogSearchApplied: '',
	catalogRowAction: {},
	thresholdEditOpen: false,
	thresholdInput: '',
	thresholdSubmitting: false,
});

interface State {
	activeAsset: AssetKind;
	stock: AssetBucket;
	crypto: AssetBucket;

	// Modal: editar (global — sólo uno abierto a la vez)
	editModalOpen: boolean;
	editAsset: AssetKind | null;
	editStockRow: CatalogRow | null;
	editForm: { name: string; priority: string };
	editSubmitting: boolean;

	// Modal: ver usuarios (global)
	usersModalOpen: boolean;
	usersModalAsset: AssetKind | null;
	usersModalStock: CatalogRow | null;
	usersModalLoading: boolean;
	usersModalError: boolean;
	usersModalData: CatalogUsersResponse | null;

	// Compartido
	lastUpdated: Date | null;
	tick: number;
}

class Stocks extends React.Component<{}, State> {

	refreshTimer: any = null;
	tickTimer: any = null;
	searchDebounceTimers: Record<AssetKind, any> = { stock: null, crypto: null };
	catalogSearchDebounceTimers: Record<AssetKind, any> = { stock: null, crypto: null };
	isMounted_: boolean = false;

	state: State = {
		activeAsset: 'stock',
		stock: makeEmptyBucket(),
		crypto: makeEmptyBucket(),

		editModalOpen: false,
		editAsset: null,
		editStockRow: null,
		editForm: { name: '', priority: '' },
		editSubmitting: false,

		usersModalOpen: false,
		usersModalAsset: null,
		usersModalStock: null,
		usersModalLoading: false,
		usersModalError: false,
		usersModalData: null,

		lastUpdated: null,
		tick: Date.now(),
	}

	componentDidMount() {
		this.isMounted_ = true;
		// Carga inicial del tab actual (stock por defecto).
		this.loadStale('stock', true);
		this.loadCatalog('stock', true);

		this.refreshTimer = setInterval(() => {
			// Solo auto-refresh del stale del tab activo: catálogo no se
			// auto-refresca (es un listado, no datos en vivo), y el tab
			// inactivo queda congelado hasta que el usuario vuelva.
			this.loadStale(this.state.activeAsset, false);
		}, REFRESH_INTERVAL_MS);
		this.tickTimer = setInterval(() => {
			if (this.isMounted_) this.setState({ tick: Date.now() });
		}, TICK_INTERVAL_MS);
	}

	componentWillUnmount() {
		this.isMounted_ = false;
		if (this.refreshTimer) clearInterval(this.refreshTimer);
		if (this.tickTimer) clearInterval(this.tickTimer);
		(['stock', 'crypto'] as AssetKind[]).forEach((a) => {
			if (this.searchDebounceTimers[a]) clearTimeout(this.searchDebounceTimers[a]);
			if (this.catalogSearchDebounceTimers[a]) clearTimeout(this.catalogSearchDebounceTimers[a]);
		});
		this.refreshTimer = null;
		this.tickTimer = null;
	}

	// ============================================================
	// Helpers de state por asset
	// ============================================================

	// Patch parcial sobre el bucket de un asset. Centraliza el shape
	// (s) => ({ [asset]: { ...s[asset], ...patch } }) para evitar
	// errores de tipo y mantener immutabilidad.
	patchBucket = (asset: AssetKind, patch: Partial<AssetBucket>, cb?: () => void) => {
		this.setState((s) => ({
			[asset]: { ...s[asset], ...patch },
		} as any), cb);
	}

	getBucket = (asset: AssetKind): AssetBucket => this.state[asset];

	// ============================================================
	// Asset switcher
	// ============================================================

	switchAsset = (asset: AssetKind) => {
		if (asset === this.state.activeAsset) return;
		this.setState({ activeAsset: asset }, () => {
			// Si el bucket recién mostrado no tiene data, dispara su
			// primera carga. Si ya la tiene, no re-pega al backend
			// (el usuario puede usar el botón "Refrescar" si lo necesita).
			const b = this.getBucket(asset);
			if (b.staleData === null) this.loadStale(asset, true);
			if (b.catalogData === null) this.loadCatalog(asset, true);
		});
	}

	// ============================================================
	// SECCIÓN B — Stale
	// ============================================================

	loadStale = async (asset: AssetKind, isFirst: boolean) => {
		const call = asset === 'stock'
			? SystemService.admin.getStaleStocks({ withoutLoading: true })
			: SystemService.admin.getStaleCryptos({ withoutLoading: true });

		try {
			const res: any = await call;
			if (!this.isMounted_) return;
			const cur = this.getBucket(asset);
			this.patchBucket(asset, {
				staleData: res || null,
				staleLoadError: false,
				staleLoading: isFirst ? false : cur.staleLoading,
			});
			this.setState({ lastUpdated: new Date() });
		}
		catch (e) {
			if (!this.isMounted_) return;
			const cur = this.getBucket(asset);
			this.patchBucket(asset, {
				staleLoadError: true,
				staleLoading: isFirst ? false : cur.staleLoading,
			});
		}
	}

	manualRefresh = () => {
		const active = this.state.activeAsset;
		if (this.refreshTimer) clearInterval(this.refreshTimer);
		this.loadStale(active, false);
		this.refreshTimer = setInterval(() => this.loadStale(this.state.activeAsset, false), REFRESH_INTERVAL_MS);
	}

	switchStaleTab = (asset: AssetKind, tab: StaleTab) => {
		if (tab === this.getBucket(asset).staleTab) return;
		this.patchBucket(asset, { staleTab: tab });
	}

	// ============================================================
	// SECCIÓN A — Búsqueda + Add
	// ============================================================

	onSearchChange = (asset: AssetKind, value: string) => {
		this.patchBucket(asset, { searchInput: value, searchError: null });
		if (this.searchDebounceTimers[asset]) clearTimeout(this.searchDebounceTimers[asset]);

		const trimmed = value.trim();
		if (trimmed.length < MIN_SEARCH_LEN) {
			this.patchBucket(asset, { searchApplied: '', searchMatches: [], searchLoading: false });
			return;
		}

		this.searchDebounceTimers[asset] = setTimeout(() => {
			if (!this.isMounted_) return;
			this.runSearch(asset, trimmed);
		}, SEARCH_DEBOUNCE_MS);
	}

	runSearch = async (asset: AssetKind, query: string) => {
		this.patchBucket(asset, { searchApplied: query, searchLoading: true, searchError: null });

		try {
			let matches: SearchMatch[] | CryptoSearchMatch[];
			if (asset === 'stock') {
				const res: any = await SystemService.admin.searchStocks({ query, withoutLoading: true });
				if (!this.isMounted_) return;
				if (this.getBucket('stock').searchApplied !== query) return;
				const raw: SearchMatch[] = Array.isArray(res?.matches) ? res.matches : [];
				matches = sortMatches(raw);
			}
			else {
				// Cripto: backend espera `symbol` (no `query`). En la práctica
				// el match es exacto, no parcial — por eso el placeholder dice
				// "Símbolo exacto".
				const res: any = await SystemService.admin.searchCryptos({ symbol: query, withoutLoading: true });
				if (!this.isMounted_) return;
				if (this.getBucket('crypto').searchApplied !== query) return;
				const raw: CryptoSearchMatch[] = Array.isArray(res?.matches) ? res.matches : [];
				matches = sortCryptoMatches(raw);
			}
			this.patchBucket(asset, { searchMatches: matches, searchLoading: false });
		}
		catch (e: any) {
			if (!this.isMounted_) return;
			const status = e?.response?.status;
			const backendMsg = e?.response?.data?.error || e?.response?.data?.message;
			let msg = backendMsg || 'No se pudo buscar.';
			if (status === 502) {
				msg = backendMsg || (asset === 'stock'
					? 'El proveedor (Alpha Vantage) no respondió. Intenta de nuevo.'
					: 'El proveedor (CoinMarketCap) no respondió. Intenta de nuevo.');
			}
			this.patchBucket(asset, { searchLoading: false, searchError: msg, searchMatches: [] });
		}
	}

	// ---------- Add: stock individual ----------
	addOneStock = async (m: SearchMatch) => {
		const symbol = (m.symbol || '').trim();
		if (!symbol) return;
		if (this.getBucket('stock').addingKey[symbol]) return;

		this.patchBucket('stock', {
			addingKey: { ...this.getBucket('stock').addingKey, [symbol]: true },
		});
		try {
			const names: Record<string, string> = {};
			if (m.name) names[symbol] = m.name;
			const res: any = await SystemService.admin.addStocks({ symbols: [symbol], names });
			if (!this.isMounted_) return;
			this.patchBucket('stock', { lastAddResult: res || null });
			this.reportAddResult(res);
			this.loadStale('stock', false);
			this.loadCatalog('stock', false);
		}
		catch (e: any) {
			if (!this.isMounted_) return;
			const backendMsg = e?.response?.data?.error || e?.response?.data?.message;
			Globals.showError(backendMsg || `No se pudo agregar ${ symbol }.`);
		}
		finally {
			if (!this.isMounted_) return;
			const copy = { ...this.getBucket('stock').addingKey };
			delete copy[symbol];
			this.patchBucket('stock', { addingKey: copy });
		}
	}

	// ---------- Add: stock bulk (textarea) ----------
	addBulkStock = async () => {
		const bucket = this.getBucket('stock');
		const raw = bucket.bulkRaw.trim();
		if (!raw) {
			Globals.showWarning('Pega al menos un símbolo en el campo.');
			return;
		}
		if (bucket.bulkSubmitting) return;

		this.patchBucket('stock', { bulkSubmitting: true });
		try {
			const res: any = await SystemService.admin.addStocks({ raw });
			if (!this.isMounted_) return;
			this.patchBucket('stock', { lastAddResult: res || null, bulkRaw: '' });
			this.reportAddResult(res);
			this.loadStale('stock', false);
			this.loadCatalog('stock', false);
		}
		catch (e: any) {
			if (!this.isMounted_) return;
			const backendMsg = e?.response?.data?.error || e?.response?.data?.message;
			Globals.showError(backendMsg || 'No se pudo procesar la lista.');
		}
		finally {
			if (!this.isMounted_) return;
			this.patchBucket('stock', { bulkSubmitting: false });
		}
	}

	// ---------- Add: crypto individual por CMC id ----------
	addOneCrypto = async (m: CryptoSearchMatch) => {
		const id = m.id;
		if (id === undefined || id === null) return;
		if (m.ya_existe) return; // doble guard: el botón ya está disabled
		const key = String(id);
		if (this.getBucket('crypto').addingKey[key]) return;

		this.patchBucket('crypto', {
			addingKey: { ...this.getBucket('crypto').addingKey, [key]: true },
		});
		try {
			const res: any = await SystemService.admin.addCryptos({ ids: [id] });
			if (!this.isMounted_) return;
			this.patchBucket('crypto', { lastAddResult: res || null });
			this.reportAddResult(res);

			// Tras agregar exitosamente, marcamos esta match como ya_existe
			// localmente para que el botón se vuelva "Ya existe" sin esperar
			// otra búsqueda. UX más responsiva.
			const matches = this.getBucket('crypto').searchMatches as CryptoSearchMatch[];
			const updated = matches.map((x) => x.id === id ? { ...x, ya_existe: true } : x);
			this.patchBucket('crypto', { searchMatches: updated });

			this.loadStale('crypto', false);
			this.loadCatalog('crypto', false);
		}
		catch (e: any) {
			if (!this.isMounted_) return;
			const backendMsg = e?.response?.data?.error || e?.response?.data?.message;
			Globals.showError(backendMsg || `No se pudo agregar ${ m.symbol || m.name || id }.`);
		}
		finally {
			if (!this.isMounted_) return;
			const copy = { ...this.getBucket('crypto').addingKey };
			delete copy[key];
			this.patchBucket('crypto', { addingKey: copy });
		}
	}

	// Resume el resultado de un add en un toast principal + un warning
	// si hubo símbolos problemáticos. El panel inferior (lastAddResult)
	// guarda el detalle completo para inspección.
	reportAddResult = (res: AddResponse | null) => {
		if (!res) return;
		const c = res.counts || {};
		const addedCount = Number(c.agregados ?? (res.agregados?.length || 0));
		const dupCount   = Number(c.duplicados ?? (res.duplicados?.length || 0));
		const noPriceCnt = Number(c.agregados_sin_precio ?? (res.agregados_sin_precio?.length || 0));
		const failedCnt  = Number(c.fallidos ?? (res.fallidos?.length || 0));

		const summary = [
			`${ addedCount } agregado${ addedCount === 1 ? '' : 's' }`,
			`${ dupCount } duplicado${ dupCount === 1 ? '' : 's' }`,
			noPriceCnt > 0 ? `${ noPriceCnt } sin precio` : null,
			failedCnt  > 0 ? `${ failedCnt } fallido${ failedCnt === 1 ? '' : 's' }`  : null,
		].filter(Boolean).join(' · ');

		Globals.showSuccess(summary || 'Procesado.');

		if (failedCnt > 0) {
			const list = (res.fallidos || []).slice(0, 5)
				.map((f) => `${ f.symbol || '?' }${ f.error ? ': ' + f.error : '' }`)
				.join('\n');
			const more = failedCnt > 5 ? `\n…y ${ failedCnt - 5 } más` : '';
			if (addedCount > 0 || dupCount > 0 || noPriceCnt > 0) {
				Globals.showWarning(`Algunos símbolos fallaron:\n${ list }${ more }`);
			}
		}
	}

	clearLastAddResult = (asset: AssetKind) => {
		this.patchBucket(asset, { lastAddResult: null });
	}

	// ============================================================
	// SECCIÓN B — Acciones por fila (retry / delete) — Stale
	// ============================================================

	retryDead = async (asset: AssetKind, row: StaleRow) => {
		const id = row.id;
		if (id === undefined || id === null) return;
		const key = String(id);
		const bucket = this.getBucket(asset);
		if (bucket.rowAction[key]) return;

		this.patchBucket(asset, {
			rowAction: { ...bucket.rowAction, [key]: 'retry' },
		});

		try {
			const res: any = asset === 'stock'
				? await SystemService.admin.deadAction({ action: 'retry', stock_id: id })
				: await SystemService.admin.cryptoDeadAction({ action: 'retry', crypto_id: id });
			if (!this.isMounted_) return;
			const status = res?.status;
			if (status === 'actualizado') {
				const price = res?.price !== undefined
					? (asset === 'stock' ? ` ($${ formatStockPrice(res.price) })` : ` (${ formatCryptoPrice(res.price) })`)
					: '';
				Globals.showSuccess(`${ row.symbol || (asset === 'stock' ? 'Stock' : 'Crypto') } actualizado${ price }.`);
			}
			else if (status === 'sigue_sin_cotizar') {
				Globals.showWarning(`${ row.symbol || (asset === 'stock' ? 'Stock' : 'Crypto') } sigue sin cotizar.`);
			}
			else {
				Globals.showSuccess(`${ row.symbol || 'OK' }: ${ status || 'OK' }`);
			}
			this.loadStale(asset, false);
		}
		catch (e: any) {
			if (!this.isMounted_) return;
			const backendMsg = e?.response?.data?.error || e?.response?.data?.message;
			Globals.showError(backendMsg || `No se pudo reintentar ${ row.symbol || 'el ítem' }.`);
		}
		finally {
			if (!this.isMounted_) return;
			const copy = { ...this.getBucket(asset).rowAction };
			delete copy[key];
			this.patchBucket(asset, { rowAction: copy });
		}
	}

	deleteDead = (asset: AssetKind, row: StaleRow) => {
		const id = row.id;
		if (id === undefined || id === null) return;
		const label = row.symbol || (asset === 'stock' ? 'este stock' : 'esta crypto');
		Globals.confirm(`¿Eliminar ${ label } del catálogo? Esta acción no se puede deshacer.`, async () => {
			const key = String(id);
			const bucket = this.getBucket(asset);
			if (bucket.rowAction[key]) return;

			this.patchBucket(asset, {
				rowAction: { ...bucket.rowAction, [key]: 'delete' },
			});

			try {
				const res: any = asset === 'stock'
					? await SystemService.admin.deadAction({ action: 'delete', stock_id: id })
					: await SystemService.admin.cryptoDeadAction({ action: 'delete', crypto_id: id });
				if (!this.isMounted_) return;
				if (res?.ok) {
					Globals.showSuccess(`${ label } eliminado del catálogo.`);
				}
				else if (res?.reason === 'en_uso') {
					Globals.showWarning(`No se puede eliminar ${ label }: está en uso por usuarios.`);
				}
				else {
					Globals.showWarning(`No se pudo eliminar ${ label }.`);
				}
				this.loadStale(asset, false);
				this.loadCatalog(asset, false);
			}
			catch (e: any) {
				if (!this.isMounted_) return;
				const backendMsg = e?.response?.data?.error || e?.response?.data?.message;
				Globals.showError(backendMsg || `No se pudo eliminar ${ label }.`);
			}
			finally {
				if (!this.isMounted_) return;
				const copy = { ...this.getBucket(asset).rowAction };
				delete copy[key];
				this.patchBucket(asset, { rowAction: copy });
			}
		});
	}

	// ============================================================
	// SECCIÓN C — Catálogo
	// ============================================================

	loadCatalog = async (asset: AssetKind, isFirst: boolean) => {
		const bucket = this.getBucket(asset);
		const params: any = {
			page: bucket.catalogPage,
			per_page: bucket.catalogPerPage,
			order_by: bucket.catalogOrderBy,
			order_dir: bucket.catalogOrderDir,
			withoutLoading: true,
		};
		if (bucket.catalogSearchApplied) params.search = bucket.catalogSearchApplied;

		try {
			const res: any = asset === 'stock'
				? await SystemService.admin.listStocks(params)
				: await SystemService.admin.listCryptos(params);
			if (!this.isMounted_) return;
			const cur = this.getBucket(asset);
			this.patchBucket(asset, {
				catalogData: res || null,
				catalogLoadError: false,
				catalogLoading: isFirst ? false : cur.catalogLoading,
			});
			this.setState({ lastUpdated: new Date() });
		}
		catch (e) {
			if (!this.isMounted_) return;
			const cur = this.getBucket(asset);
			this.patchBucket(asset, {
				catalogLoadError: true,
				catalogLoading: isFirst ? false : cur.catalogLoading,
			});
		}
	}

	refreshCatalog = (asset: AssetKind) => {
		this.loadCatalog(asset, false);
	}

	onCatalogSearchChange = (asset: AssetKind, value: string) => {
		this.patchBucket(asset, { catalogSearch: value });
		if (this.catalogSearchDebounceTimers[asset]) clearTimeout(this.catalogSearchDebounceTimers[asset]);
		this.catalogSearchDebounceTimers[asset] = setTimeout(() => {
			if (!this.isMounted_) return;
			this.patchBucket(asset, { catalogSearchApplied: value, catalogPage: 1 }, () => this.loadCatalog(asset, false));
		}, SEARCH_DEBOUNCE_MS);
	}

	onCatalogOrderByChange = (asset: AssetKind, orderBy: CatalogOrderBy) => {
		if (orderBy === this.getBucket(asset).catalogOrderBy) return;
		this.patchBucket(asset, { catalogOrderBy: orderBy, catalogPage: 1 }, () => this.loadCatalog(asset, false));
	}

	onCatalogOrderDirChange = (asset: AssetKind, dir: CatalogOrderDir) => {
		if (dir === this.getBucket(asset).catalogOrderDir) return;
		this.patchBucket(asset, { catalogOrderDir: dir, catalogPage: 1 }, () => this.loadCatalog(asset, false));
	}

	onCatalogPerPageChange = (asset: AssetKind, perPage: number) => {
		if (perPage === this.getBucket(asset).catalogPerPage) return;
		this.patchBucket(asset, { catalogPerPage: perPage, catalogPage: 1 }, () => this.loadCatalog(asset, false));
	}

	goToCatalogPage = (asset: AssetKind, page: number) => {
		if (page < 1) return;
		if (page === this.getBucket(asset).catalogPage) return;
		this.patchBucket(asset, { catalogPage: page }, () => this.loadCatalog(asset, false));
	}

	// ---------- Modal: editar ----------

	openEditModal = (asset: AssetKind, row: CatalogRow) => {
		this.setState({
			editModalOpen: true,
			editAsset: asset,
			editStockRow: row,
			editForm: {
				name: row.name || '',
				priority: row.priority !== null && row.priority !== undefined ? String(row.priority) : '',
			},
		});
	}

	closeEditModal = () => {
		if (this.state.editSubmitting) return;
		this.setState({
			editModalOpen: false,
			editAsset: null,
			editStockRow: null,
			editForm: { name: '', priority: '' },
		});
	}

	onEditFieldChange = (field: 'name' | 'priority', value: string) => {
		this.setState((s) => ({ editForm: { ...s.editForm, [field]: value } }));
	}

	submitEdit = async () => {
		const { editStockRow, editForm, editAsset } = this.state;
		if (!editStockRow || !editAsset || editStockRow.id === undefined || editStockRow.id === null) return;

		const name = editForm.name.trim();
		const priorityStr = editForm.priority.trim();

		const params: any = editAsset === 'stock'
			? { stock_id: editStockRow.id }
			: { crypto_id: editStockRow.id };
		let hasChanges = false;

		if (name && name !== (editStockRow.name || '')) {
			params.name = name;
			hasChanges = true;
		}

		if (priorityStr !== '') {
			if (!/^\d+$/.test(priorityStr)) {
				Globals.showWarning('Priority debe ser un número entero ≥ 0.');
				return;
			}
			const priorityNum = parseInt(priorityStr, 10);
			if (!isFinite(priorityNum) || priorityNum < 0) {
				Globals.showWarning('Priority debe ser un número entero ≥ 0.');
				return;
			}
			const currentPriority = editStockRow.priority;
			if (priorityNum !== currentPriority) {
				params.priority = priorityNum;
				hasChanges = true;
			}
		}

		if (!hasChanges) {
			Globals.showWarning('No hay cambios para guardar.');
			return;
		}

		this.setState({ editSubmitting: true });
		try {
			const res: any = editAsset === 'stock'
				? await SystemService.admin.editStock(params)
				: await SystemService.admin.editCrypto(params);
			if (!this.isMounted_) return;
			this.setState({
				editSubmitting: false,
				editModalOpen: false,
				editAsset: null,
				editStockRow: null,
				editForm: { name: '', priority: '' },
			});
			if (res?.ok) {
				const label = editStockRow.symbol
					|| (editAsset === 'stock' ? res?.stock?.symbol : res?.crypto?.symbol)
					|| (editAsset === 'stock' ? 'Stock' : 'Crypto');
				Globals.showSuccess(`${ label } actualizado.`);
			}
			else {
				Globals.showWarning('No se pudo guardar los cambios.');
			}
			this.loadCatalog(editAsset, false);
		}
		catch (e: any) {
			if (!this.isMounted_) return;
			this.setState({ editSubmitting: false });
			const backendMsg = e?.response?.data?.error || e?.response?.data?.message;
			Globals.showError(backendMsg || 'No se pudo guardar los cambios.');
		}
	}

	// ---------- Acción del catálogo: eliminar ----------

	deleteCatalogRow = (asset: AssetKind, row: CatalogRow) => {
		const id = row.id;
		if (id === undefined || id === null) return;
		const label = row.symbol || (asset === 'stock' ? 'este stock' : 'esta crypto');
		Globals.confirm(`¿Eliminar ${ label } del catálogo? Esta acción no se puede deshacer.`, async () => {
			const key = String(id);
			const bucket = this.getBucket(asset);
			if (bucket.catalogRowAction[key]) return;
			this.patchBucket(asset, {
				catalogRowAction: { ...bucket.catalogRowAction, [key]: 'delete' },
			});
			try {
				const res: any = asset === 'stock'
					? await SystemService.admin.deadAction({ action: 'delete', stock_id: id })
					: await SystemService.admin.cryptoDeadAction({ action: 'delete', crypto_id: id });
				if (!this.isMounted_) return;
				if (res?.ok) {
					Globals.showSuccess(`${ label } eliminado del catálogo.`);
				}
				else if (res?.reason === 'en_uso') {
					Globals.showWarning(`No se puede eliminar ${ label }: está en uso por usuarios.`);
				}
				else {
					Globals.showWarning(`No se pudo eliminar ${ label }.`);
				}
				this.loadCatalog(asset, false);
				this.loadStale(asset, false);
			}
			catch (e: any) {
				if (!this.isMounted_) return;
				const backendMsg = e?.response?.data?.error || e?.response?.data?.message;
				Globals.showError(backendMsg || `No se pudo eliminar ${ label }.`);
			}
			finally {
				if (!this.isMounted_) return;
				const copy = { ...this.getBucket(asset).catalogRowAction };
				delete copy[key];
				this.patchBucket(asset, { catalogRowAction: copy });
			}
		});
	}

	// ---------- Modal: usuarios ----------

	openUsersModal = async (asset: AssetKind, row: CatalogRow) => {
		const id = row.id;
		if (id === undefined || id === null) return;
		this.setState({
			usersModalOpen: true,
			usersModalAsset: asset,
			usersModalStock: row,
			usersModalLoading: true,
			usersModalError: false,
			usersModalData: null,
		});
		try {
			const res: any = asset === 'stock'
				? await SystemService.admin.stockUsers({ stock_id: id, withoutLoading: true })
				: await SystemService.admin.cryptoUsers({ crypto_id: id, withoutLoading: true });
			if (!this.isMounted_) return;
			if (!this.state.usersModalOpen) return;
			this.setState({ usersModalData: res || null, usersModalLoading: false });
		}
		catch (e) {
			if (!this.isMounted_) return;
			this.setState({ usersModalError: true, usersModalLoading: false });
		}
	}

	closeUsersModal = () => {
		this.setState({
			usersModalOpen: false,
			usersModalAsset: null,
			usersModalStock: null,
			usersModalLoading: false,
			usersModalError: false,
			usersModalData: null,
		});
	}

	// ============================================================
	// SECCIÓN B — Editar umbral (threshold_hours) por asset
	// ============================================================

	openThresholdEditor = (asset: AssetKind) => {
		const cur = this.getBucket(asset);
		const current = Number(cur.staleData?.threshold_hours);
		this.patchBucket(asset, {
			thresholdEditOpen: true,
			thresholdInput: isFinite(current) && current > 0 ? String(current) : '',
		});
	}

	cancelThresholdEditor = (asset: AssetKind) => {
		if (this.getBucket(asset).thresholdSubmitting) return;
		this.patchBucket(asset, {
			thresholdEditOpen: false,
			thresholdInput: '',
		});
	}

	onThresholdInputChange = (asset: AssetKind, value: string) => {
		// Solo dígitos (campo numérico simple).
		if (value === '' || /^\d+$/.test(value)) {
			this.patchBucket(asset, { thresholdInput: value });
		}
	}

	submitThreshold = async (asset: AssetKind) => {
		const bucket = this.getBucket(asset);
		const raw = bucket.thresholdInput.trim();
		if (raw === '') {
			Globals.showWarning('Ingresa un número de horas (entero ≥ 1).');
			return;
		}
		if (!/^\d+$/.test(raw)) {
			Globals.showWarning('El umbral debe ser un número entero ≥ 1.');
			return;
		}
		const hours = parseInt(raw, 10);
		if (!isFinite(hours) || hours < 1) {
			Globals.showWarning('El umbral debe ser un entero ≥ 1.');
			return;
		}

		const current = Number(bucket.staleData?.threshold_hours);
		if (isFinite(current) && hours === current) {
			Globals.showWarning('El umbral ya tiene ese valor.');
			return;
		}

		if (bucket.thresholdSubmitting) return;
		this.patchBucket(asset, { thresholdSubmitting: true });

		try {
			const res: any = asset === 'stock'
				? await SystemService.admin.setStockThreshold({ hours })
				: await SystemService.admin.setCryptoThreshold({ hours });
			if (!this.isMounted_) return;
			if (res?.ok) {
				const newHours = Number(res?.hours);
				const shown = isFinite(newHours) ? newHours : hours;
				Globals.showSuccess(`Umbral actualizado a ${ shown }h.`);
				this.patchBucket(asset, {
					thresholdSubmitting: false,
					thresholdEditOpen: false,
					thresholdInput: '',
				});
				// Recargar stale del asset para reflejar nuevo threshold +
				// conteos recalculados (la frontera muertas/pendientes se mueve).
				this.loadStale(asset, false);
			}
			else {
				this.patchBucket(asset, { thresholdSubmitting: false });
				Globals.showWarning('No se pudo guardar el umbral.');
			}
		}
		catch (e: any) {
			if (!this.isMounted_) return;
			this.patchBucket(asset, { thresholdSubmitting: false });
			const backendMsg = e?.response?.data?.error || e?.response?.data?.message;
			Globals.showError(backendMsg || 'No se pudo guardar el umbral.');
		}
	}

	// ============================================================
	// Render
	// ============================================================

	renderHeader() {
		const { activeAsset, lastUpdated } = this.state;
		const bucket = this.getBucket(activeAsset);
		const initialLoading = bucket.staleLoading && !bucket.staleData;
		const lastLabel = lastUpdated
			? moment(lastUpdated).utcOffset(-4).format('HH:mm:ss')
			: '—';

		return (
			<div className="stocks-header">
				<div className="stocks-header-text">
					<h1>Activos</h1>
					<p>{ COPY[activeAsset].pageDesc }</p>
				</div>
				<div className="stocks-header-actions">
					<span className="refresh-badge">
						<span className="refresh-dot"></span>
						Actualizando cada 30s · { lastLabel }
					</span>
					<button
						className="dashboard-refresh-btn"
						onClick={ this.manualRefresh }
						disabled={ initialLoading }>
						{ initialLoading ? 'Cargando…' : 'Refrescar' }
					</button>
				</div>
			</div>
		);
	}

	renderAssetTabs() {
		const { activeAsset } = this.state;
		return (
			<div className="stocks-asset-tabs">
				{ (['stock', 'crypto'] as AssetKind[]).map((a) => (
					<button
						key={ a }
						className={ `stocks-asset-tab ${ activeAsset === a ? 'active' : '' }` }
						onClick={ () => this.switchAsset(a) }>
						{ COPY[a].tabLabel }
					</button>
				)) }
			</div>
		);
	}

	// ---------- Sección A ----------

	renderSearchSection() {
		const { activeAsset } = this.state;
		const bucket = this.getBucket(activeAsset);
		const copy = COPY[activeAsset];

		const showHint  = bucket.searchInput.trim().length > 0 && bucket.searchInput.trim().length < MIN_SEARCH_LEN;
		const showEmpty = !bucket.searchLoading && !bucket.searchError && bucket.searchInput.trim().length >= MIN_SEARCH_LEN && bucket.searchMatches.length === 0;

		return (
			<div className="stocks-section stocks-search-section">
				<div className="stocks-section__head">
					<h2 className="stocks-section__title">{ copy.searchSectionTitle }</h2>
					<span className="stocks-section__desc">{ copy.searchSectionDesc }</span>
				</div>

				<div className="stocks-search-controls">
					<div className="logs-filter-field logs-filter-search">
						<label>{ copy.searchLabel }</label>
						<div className="logs-search-wrap">
							<span className="logs-search-icon" aria-hidden="true">⌕</span>
							<input
								type="text"
								className="filter-input"
								placeholder={ copy.searchPlaceholder }
								value={ bucket.searchInput }
								onChange={ (e) => this.onSearchChange(activeAsset, e.target.value) }
							/>
						</div>
						{ showHint && <span className="stocks-hint">Mínimo { MIN_SEARCH_LEN } caracteres.</span> }
					</div>
				</div>

				<div className="stocks-search-results">
					{ bucket.searchLoading && (
						<div className="stocks-empty">Buscando…</div>
					) }
					{ !bucket.searchLoading && bucket.searchError && (
						<div className="cron-error-card">{ bucket.searchError }</div>
					) }
					{ !bucket.searchLoading && !bucket.searchError && bucket.searchMatches.length > 0 && (
						activeAsset === 'stock'
							? this.renderStockMatches(bucket.searchMatches as SearchMatch[])
							: this.renderCryptoMatches(bucket.searchMatches as CryptoSearchMatch[])
					) }
					{ showEmpty && (
						<div className="stocks-empty">Sin resultados para “{ bucket.searchInput.trim() }”.</div>
					) }
				</div>

				{ activeAsset === 'stock' && this.renderStockBulk() }

				{ bucket.lastAddResult && this.renderAddResult(activeAsset, bucket.lastAddResult) }
			</div>
		);
	}

	renderStockMatches(matches: SearchMatch[]) {
		const bucket = this.getBucket('stock');
		return (
			<ul className="stocks-match-list">
				{ matches.map((m, idx) => {
					const symbol = m.symbol || '';
					const isAdding = !!(symbol && bucket.addingKey[symbol]);
					const us = isUS(m);
					return (
						<li className={ `stocks-match ${ us ? 'is-us' : '' }` } key={ `${ symbol }-${ idx }` }>
							<div className="stocks-match__main">
								<span className="stocks-match__symbol">{ symbol || '—' }</span>
								<span className="stocks-match__name" title={ m.name }>{ m.name || '—' }</span>
							</div>
							<div className="stocks-match__meta">
								{ m.region   && <span className="stocks-meta-pill">{ m.region }</span> }
								{ m.currency && <span className="stocks-meta-pill mono">{ m.currency }</span> }
								{ m.type     && <span className="stocks-meta-pill ghost">{ m.type }</span> }
							</div>
							<div className="stocks-match__actions">
								<button
									className="cron-btn cron-btn-primary"
									onClick={ () => this.addOneStock(m) }
									disabled={ isAdding || !symbol }>
									{ isAdding ? 'Agregando…' : 'Agregar' }
								</button>
							</div>
						</li>
					);
				}) }
			</ul>
		);
	}

	renderCryptoMatches(matches: CryptoSearchMatch[]) {
		const bucket = this.getBucket('crypto');
		return (
			<ul className="stocks-match-list">
				{ matches.map((m, idx) => {
					const id = m.id;
					const key = id !== undefined && id !== null ? String(id) : `${ m.symbol }-${ idx }`;
					const isAdding = !!bucket.addingKey[key];
					const hasRank = m.rank !== null && m.rank !== undefined;
					const isInactive = !(m.is_active === true || m.is_active === 1);
					const exists = !!m.ya_existe;

					// "Quality" del match para resaltado: tiene rank y está activa → real,
					// si no, es probable basura/abandono → tono apagado.
					const isLowQuality = !hasRank || isInactive;

					return (
						<li className={ `stocks-match crypto-match ${ isLowQuality ? 'is-low-quality' : 'is-quality' }` } key={ key }>
							<div className="stocks-match__main">
								<span className="stocks-match__symbol">{ m.symbol || '—' }</span>
								<span className="stocks-match__name" title={ m.name }>{ m.name || '—' }</span>
							</div>
							<div className="stocks-match__meta">
								<span className={ `stocks-meta-pill ${ hasRank ? '' : 'ghost' }` }>
									{ hasRank ? `Rank ${ m.rank }` : 'Sin rank' }
								</span>
								{ isInactive && <span className="stocks-meta-pill danger">Inactiva</span> }
								{ m.slug && <span className="stocks-meta-pill ghost mono" title={ m.slug }>{ m.slug }</span> }
								{ id !== undefined && id !== null && (
									<span className="stocks-meta-pill mono" title="CMC id">#{ id }</span>
								) }
							</div>
							<div className="stocks-match__actions">
								<button
									className={ `cron-btn ${ exists ? 'cron-btn-secondary' : 'cron-btn-primary' }` }
									onClick={ () => this.addOneCrypto(m) }
									disabled={ exists || isAdding || id === undefined || id === null }>
									{ exists ? 'Ya existe' : (isAdding ? 'Agregando…' : 'Agregar') }
								</button>
							</div>
						</li>
					);
				}) }
			</ul>
		);
	}

	renderStockBulk() {
		const bucket = this.getBucket('stock');
		return (
			<div className="stocks-bulk">
				<div className="stocks-bulk__head">
					<label className="stocks-bulk__label">Agregar lista (un símbolo por línea)</label>
					<span className="stocks-bulk__hint">Útil para cargar lotes. Ej: AAPL, MSFT, GOOG…</span>
				</div>
				<textarea
					className="stocks-bulk__textarea"
					placeholder={ 'AAPL\nMSFT\nGOOG' }
					value={ bucket.bulkRaw }
					onChange={ (e) => this.patchBucket('stock', { bulkRaw: e.target.value }) }
					rows={ 4 }
				/>
				<div className="stocks-bulk__actions">
					<button
						className="cron-btn cron-btn-ghost"
						onClick={ () => this.patchBucket('stock', { bulkRaw: '' }) }
						disabled={ bucket.bulkSubmitting || !bucket.bulkRaw }>
						Limpiar
					</button>
					<button
						className="cron-btn cron-btn-primary"
						onClick={ this.addBulkStock }
						disabled={ bucket.bulkSubmitting || !bucket.bulkRaw.trim() }>
						{ bucket.bulkSubmitting ? 'Procesando…' : 'Agregar lista' }
					</button>
				</div>
			</div>
		);
	}

	renderAddResult(asset: AssetKind, res: AddResponse) {
		const c = res.counts || {};
		const added   = Number(c.agregados ?? (res.agregados?.length || 0));
		const dup     = Number(c.duplicados ?? (res.duplicados?.length || 0));
		const noPrice = Number(c.agregados_sin_precio ?? (res.agregados_sin_precio?.length || 0));
		const failed  = Number(c.fallidos ?? (res.fallidos?.length || 0));

		return (
			<div className="stocks-add-result">
				<div className="stocks-add-result__head">
					<span className="stocks-add-result__title">Resultado</span>
					<button className="stocks-link-btn" onClick={ () => this.clearLastAddResult(asset) }>Ocultar</button>
				</div>
				<div className="stocks-add-result__pills">
					<span className="stocks-result-pill ok">Agregados { added }</span>
					<span className="stocks-result-pill neutral">Duplicados { dup }</span>
					{ noPrice > 0 && <span className="stocks-result-pill warn">Sin precio { noPrice }</span> }
					{ failed > 0  && <span className="stocks-result-pill danger">Fallidos { failed }</span> }
				</div>
				{ (failed > 0 || noPrice > 0 || dup > 0) && (
					<div className="stocks-add-result__lists">
						{ failed > 0 && (
							<div className="stocks-add-result__col">
								<span className="stocks-add-result__col-title danger">Fallidos</span>
								<ul className="mono">
									{ (res.fallidos || []).map((f, i) => (
										<li key={ `f-${ i }` }>
											<strong>{ f.symbol || '?' }</strong>{ f.error ? ` — ${ f.error }` : '' }
										</li>
									)) }
								</ul>
							</div>
						) }
						{ noPrice > 0 && (
							<div className="stocks-add-result__col">
								<span className="stocks-add-result__col-title warn">Sin precio</span>
								<ul className="mono">
									{ (res.agregados_sin_precio || []).map((x, i) => (
										<li key={ `np-${ i }` }>{ labelOfMaybeRow(x) }</li>
									)) }
								</ul>
							</div>
						) }
						{ dup > 0 && (
							<div className="stocks-add-result__col">
								<span className="stocks-add-result__col-title neutral">Duplicados</span>
								<ul className="mono">
									{ (res.duplicados || []).map((x, i) => (
										<li key={ `d-${ i }` }>{ labelOfMaybeRow(x) }</li>
									)) }
								</ul>
							</div>
						) }
					</div>
				) }
			</div>
		);
	}

	// ---------- Sección B ----------

	// Control del umbral (threshold_hours). Tiene dos modos:
	// - colapsado: muestra "Umbral: Xh" + botón "Editar".
	// - expandido: input numérico + botones "Guardar" y "Cancelar".
	// Es por-asset (cada bucket guarda su propio open/input/submitting),
	// así no hay riesgo de que el estado se pise al cambiar de tab.
	renderThresholdControl(asset: AssetKind, threshold: number) {
		const bucket = this.getBucket(asset);
		const known = isFinite(threshold);

		if (!bucket.thresholdEditOpen) {
			return (
				<span className="stocks-threshold-control">
					<span className="stocks-meta-pill">Umbral: { known ? `${ threshold }h` : '—' }</span>
					<button
						type="button"
						className="cron-btn cron-btn-ghost stocks-threshold-edit-btn"
						onClick={ () => this.openThresholdEditor(asset) }
						disabled={ bucket.staleLoading && !bucket.staleData }
						title="Editar umbral de stale">
						Editar
					</button>
				</span>
			);
		}

		const submitting = bucket.thresholdSubmitting;
		return (
			<span className="stocks-threshold-control is-editing">
				<label className="stocks-threshold-label">Umbral (horas)</label>
				<input
					type="text"
					inputMode="numeric"
					className="filter-input stocks-threshold-input"
					value={ bucket.thresholdInput }
					placeholder={ known ? String(threshold) : '24' }
					onChange={ (e) => this.onThresholdInputChange(asset, e.target.value) }
					disabled={ submitting }
					autoFocus
				/>
				<button
					type="button"
					className="cron-btn cron-btn-primary"
					onClick={ () => this.submitThreshold(asset) }
					disabled={ submitting }>
					{ submitting ? 'Guardando…' : 'Guardar umbral' }
				</button>
				<button
					type="button"
					className="cron-btn cron-btn-ghost"
					onClick={ () => this.cancelThresholdEditor(asset) }
					disabled={ submitting }>
					Cancelar
				</button>
			</span>
		);
	}

	renderStaleSection() {
		const { activeAsset } = this.state;
		const bucket = this.getBucket(activeAsset);
		const copy = COPY[activeAsset];

		const threshold = Number(bucket.staleData?.threshold_hours);
		const pendCount = Number(bucket.staleData?.counts?.pendientes ?? (bucket.staleData?.pendientes?.length || 0));
		const deadCount = Number(bucket.staleData?.counts?.muertas   ?? (bucket.staleData?.muertas?.length   || 0));

		return (
			<div className="stocks-section stocks-stale-section">
				<div className="stocks-section__head">
					<h2 className="stocks-section__title">{ copy.staleSectionTitle }</h2>
					<div className="stocks-stale__badges">
						{ this.renderThresholdControl(activeAsset, threshold) }
						<span className="stocks-meta-pill neutral">Pendientes { formatNumber(pendCount) }</span>
						<span className="stocks-meta-pill danger">Muertas { formatNumber(deadCount) }</span>
					</div>
				</div>

				<div className="stocks-stale-tabs">
					<button
						className={ `backup-tab ${ bucket.staleTab === 'muertas' ? 'active' : '' }` }
						onClick={ () => this.switchStaleTab(activeAsset, 'muertas') }>
						Muertas ({ formatNumber(deadCount) })
					</button>
					<button
						className={ `backup-tab ${ bucket.staleTab === 'pendientes' ? 'active' : '' }` }
						onClick={ () => this.switchStaleTab(activeAsset, 'pendientes') }>
						Pendientes ({ formatNumber(pendCount) })
					</button>
				</div>

				{ bucket.staleLoadError && (
					<div className="cron-error-card">
						No se pudo cargar el listado. Reintentando en 30s…
					</div>
				) }

				{ !bucket.staleLoadError && bucket.staleTab === 'muertas' && this.renderDeadTable(activeAsset, bucket) }
				{ !bucket.staleLoadError && bucket.staleTab === 'pendientes' && this.renderPendingTable(activeAsset, bucket) }

				{ bucket.staleLoading && !bucket.staleData && !bucket.staleLoadError && (
					<div className="stocks-empty">Cargando…</div>
				) }
			</div>
		);
	}

	formatRowPrice(asset: AssetKind, price: any): string {
		if (price === null || price === undefined) return '—';
		return asset === 'stock' ? `$${ formatStockPrice(price) }` : formatCryptoPrice(price);
	}

	renderDeadTable(asset: AssetKind, bucket: AssetBucket) {
		const rows = Array.isArray(bucket.staleData?.muertas) ? bucket.staleData!.muertas as StaleRow[] : [];
		const tick = this.state.tick;
		const copy = COPY[asset];

		return (
			<div className="backup-table-wrap stocks-table-wrap">
				<table className="backup-table stocks-table">
					<thead>
						<tr>
							<th>Símbolo</th>
							<th>Nombre</th>
							<th>Último precio</th>
							<th>Actualizado</th>
							<th>Creado</th>
							<th className="actions-col">Acciones</th>
						</tr>
					</thead>
					<tbody>
						{ rows.map((r, idx) => {
							const key = String(r.id ?? `${ r.symbol }-${ idx }`);
							const action = bucket.rowAction[key];
							const isRetrying = action === 'retry';
							const isDeleting = action === 'delete';
							const isBusy = isRetrying || isDeleting;

							const updated = r.paid_updated_at
								? moment(r.paid_updated_at).utcOffset(-4).format('DD/MM/YYYY HH:mm:ss')
								: '—';
							const created = r.created_at
								? moment(r.created_at).utcOffset(-4).format('DD/MM/YYYY HH:mm:ss')
								: '—';

							return (
								<tr key={ key }>
									<td className="stocks-row__symbol mono"><strong>{ r.symbol || '—' }</strong></td>
									<td className="stocks-row__name" title={ r.name }>{ r.name || '—' }</td>
									<td className="stocks-row__price mono">{ this.formatRowPrice(asset, r.price) }</td>
									<td className="stocks-row__updated" title={ updated }>
										<span className="created-rel">{ humanizeAgo(r.paid_updated_at, tick) }</span>
										<span className="created-abs mono">{ updated }</span>
									</td>
									<td className="stocks-row__created" title={ created }>
										<span className="created-rel">{ humanizeAgo(r.created_at, tick) }</span>
										<span className="created-abs mono">{ created }</span>
									</td>
									<td className="stocks-row__actions">
										<button
											className="cron-btn cron-btn-secondary"
											onClick={ () => this.retryDead(asset, r) }
											disabled={ isBusy }>
											{ isRetrying ? 'Reintentando…' : 'Reintentar' }
										</button>
										<button
											className="cron-btn cron-btn-ghost stocks-btn-danger"
											onClick={ () => this.deleteDead(asset, r) }
											disabled={ isBusy }>
											{ isDeleting ? 'Eliminando…' : 'Eliminar' }
										</button>
									</td>
								</tr>
							);
						}) }
						{ rows.length === 0 && !bucket.staleLoading && (
							<tr>
								<td colSpan={ 6 } className="empty-row">{ copy.emptyDeadLabel }</td>
							</tr>
						) }
					</tbody>
				</table>
			</div>
		);
	}

	renderPendingTable(asset: AssetKind, bucket: AssetBucket) {
		const rows = Array.isArray(bucket.staleData?.pendientes) ? bucket.staleData!.pendientes as StaleRow[] : [];
		const tick = this.state.tick;
		const copy = COPY[asset];

		return (
			<div className="backup-table-wrap stocks-table-wrap">
				<table className="backup-table stocks-table">
					<thead>
						<tr>
							<th>Símbolo</th>
							<th>Nombre</th>
							<th>Último precio</th>
							<th>Creado</th>
						</tr>
					</thead>
					<tbody>
						{ rows.map((r, idx) => {
							const key = String(r.id ?? `${ r.symbol }-${ idx }`);
							const created = r.created_at
								? moment(r.created_at).utcOffset(-4).format('DD/MM/YYYY HH:mm:ss')
								: '—';
							return (
								<tr key={ key }>
									<td className="stocks-row__symbol mono"><strong>{ r.symbol || '—' }</strong></td>
									<td className="stocks-row__name" title={ r.name }>{ r.name || '—' }</td>
									<td className="stocks-row__price mono">{ this.formatRowPrice(asset, r.price) }</td>
									<td className="stocks-row__created" title={ created }>
										<span className="created-rel">{ humanizeAgo(r.created_at, tick) }</span>
										<span className="created-abs mono">{ created }</span>
									</td>
								</tr>
							);
						}) }
						{ rows.length === 0 && !bucket.staleLoading && (
							<tr>
								<td colSpan={ 4 } className="empty-row">{ copy.emptyPendingLabel }</td>
							</tr>
						) }
					</tbody>
				</table>
			</div>
		);
	}

	// ---------- Sección C ----------

	renderCatalogSection() {
		const { activeAsset } = this.state;
		const bucket = this.getBucket(activeAsset);
		const copy = COPY[activeAsset];

		const total      = Number(bucket.catalogData?.total) || 0;
		const totalPages = Math.max(1, Number(bucket.catalogData?.count) || 1);
		const safePage   = Math.min(Math.max(bucket.catalogPage, 1), totalPages);

		return (
			<div className="stocks-section stocks-catalog-section">
				<div className="stocks-section__head">
					<h2 className="stocks-section__title">{ copy.catalogSectionTitle }</h2>
					<span className="stocks-section__desc">
						{ total > 0
							? `${ total.toLocaleString('en-US') } ${ activeAsset === 'stock' ? 'stocks' : 'cryptos' } en total`
							: 'Listado paginado · busca, ordena, edita y elimina' }
					</span>
				</div>

				<div className="stocks-catalog-controls">
					<div className="logs-filter-field logs-filter-search">
						<label>Buscar</label>
						<div className="logs-search-wrap">
							<span className="logs-search-icon" aria-hidden="true">⌕</span>
							<input
								type="text"
								className="filter-input"
								placeholder="Símbolo o nombre…"
								value={ bucket.catalogSearch }
								onChange={ (e) => this.onCatalogSearchChange(activeAsset, e.target.value) }
							/>
						</div>
					</div>
					<div className="logs-filter-field">
						<label>Ordenar por</label>
						<select
							className="filter-select"
							value={ bucket.catalogOrderBy }
							onChange={ (e) => this.onCatalogOrderByChange(activeAsset, e.target.value as CatalogOrderBy) }>
							{ ORDER_BY_OPTIONS.map((o) => (
								<option key={ o.value } value={ o.value }>{ o.label }</option>
							)) }
						</select>
					</div>
					<div className="logs-filter-field">
						<label>Dirección</label>
						<select
							className="filter-select"
							value={ bucket.catalogOrderDir }
							onChange={ (e) => this.onCatalogOrderDirChange(activeAsset, e.target.value as CatalogOrderDir) }>
							<option value="asc">Ascendente</option>
							<option value="desc">Descendente</option>
						</select>
					</div>
					<div className="logs-filter-field">
						<label>Por página</label>
						<select
							className="filter-select"
							value={ bucket.catalogPerPage }
							onChange={ (e) => this.onCatalogPerPageChange(activeAsset, Number(e.target.value)) }>
							{ CATALOG_PER_PAGE_OPTIONS.map((n) => (
								<option key={ n } value={ n }>{ n }</option>
							)) }
						</select>
					</div>
					<button
						className="cron-btn cron-btn-ghost"
						onClick={ () => this.refreshCatalog(activeAsset) }
						disabled={ bucket.catalogLoading }>
						Refrescar
					</button>
				</div>

				{ bucket.catalogLoadError && (
					<div className="cron-error-card">
						No se pudo cargar el catálogo. Intenta refrescar.
					</div>
				) }

				{ !bucket.catalogLoadError && this.renderCatalogTable(activeAsset, bucket) }

				{ !bucket.catalogLoadError && total > 0 && totalPages > 1 && (
					<div className="stocks-catalog-footer">
						<div className="stocks-catalog-footer__info">
							Página <strong>{ safePage }</strong> de <strong>{ totalPages }</strong>
						</div>
						<div className="stocks-catalog-footer__pagination">
							<Pagination
								active={ safePage }
								pages={ totalPages }
								onChange={ (p: number) => this.goToCatalogPage(activeAsset, p) }
							/>
						</div>
					</div>
				) }
			</div>
		);
	}

	renderCatalogTable(asset: AssetKind, bucket: AssetBucket) {
		const rows = Array.isArray(bucket.catalogData?.rows) ? bucket.catalogData!.rows as CatalogRow[] : [];
		const tick = this.state.tick;

		return (
			<div className="backup-table-wrap stocks-table-wrap stocks-catalog-table-wrap">
				<table className="backup-table stocks-table stocks-catalog-table">
					<thead>
						<tr>
							<th>Símbolo</th>
							<th>Nombre</th>
							<th>Precio</th>
							<th>%</th>
							<th>Priority</th>
							<th>Usuarios</th>
							<th>Actualizado</th>
							<th className="actions-col">Acciones</th>
						</tr>
					</thead>
					<tbody>
						{ rows.map((r, idx) => {
							const key = String(r.id ?? `${ r.symbol }-${ idx }`);
							const action = bucket.catalogRowAction[key];
							const isDeleting = action === 'delete';
							const isBusy = !!action;

							const pct = formatPercent(r.percent);
							const usersCount = Number(r.usuarios_count) || 0;
							const updated = r.paid_updated_at
								? moment(r.paid_updated_at).utcOffset(-4).format('DD/MM/YYYY HH:mm:ss')
								: '—';

							return (
								<tr key={ key }>
									<td className="stocks-row__symbol mono"><strong>{ r.symbol || '—' }</strong></td>
									<td className="stocks-row__name" title={ r.name }>{ r.name || '—' }</td>
									<td className="stocks-row__price mono">{ this.formatRowPrice(asset, r.price) }</td>
									<td className={ `stocks-row__percent mono stocks-pct-${ pct.cls }` }>{ pct.text }</td>
									<td className="stocks-row__priority mono">
										{ r.priority !== null && r.priority !== undefined ? r.priority : '—' }
									</td>
									<td className="stocks-row__users">
										<button
											type="button"
											className={ `stocks-users-badge ${ usersCount === 0 ? 'is-empty' : '' }` }
											onClick={ () => this.openUsersModal(asset, r) }
											title={ usersCount > 0 ? `Ver ${ usersCount } usuarios` : 'Sin usuarios' }>
											{ usersCount }
										</button>
									</td>
									<td className="stocks-row__updated" title={ updated }>
										<span className="created-rel">{ humanizeAgo(r.paid_updated_at, tick) }</span>
										<span className="created-abs mono">{ updated }</span>
									</td>
									<td className="stocks-row__actions">
										<button
											className="cron-btn cron-btn-secondary"
											onClick={ () => this.openEditModal(asset, r) }
											disabled={ isBusy }>
											Editar
										</button>
										<button
											className="cron-btn cron-btn-ghost stocks-btn-danger"
											onClick={ () => this.deleteCatalogRow(asset, r) }
											disabled={ isBusy }>
											{ isDeleting ? 'Eliminando…' : 'Eliminar' }
										</button>
									</td>
								</tr>
							);
						}) }
						{ rows.length === 0 && !bucket.catalogLoading && (
							<tr><td colSpan={ 8 } className="empty-row">No hay registros que coincidan con los filtros.</td></tr>
						) }
						{ rows.length === 0 && bucket.catalogLoading && (
							<tr><td colSpan={ 8 } className="empty-row">Cargando…</td></tr>
						) }
					</tbody>
				</table>
			</div>
		);
	}

	// ---------- Modal: editar ----------

	renderEditModal() {
		const { editModalOpen, editStockRow, editForm, editSubmitting, editAsset } = this.state;
		if (!editModalOpen || !editStockRow || !editAsset) return null;

		return (
			<Modal
				className="modal-stocks-edit"
				visible={ editModalOpen }
				title={ `Editar ${ editStockRow.symbol || (editAsset === 'stock' ? 'stock' : 'crypto') }` }
				onClose={ this.closeEditModal }>
				<div className="modal-stocks-body">
					<div className="modal-stocks-field">
						<label>Símbolo</label>
						<code className="modal-stocks-value mono">{ editStockRow.symbol || '—' }</code>
						<span className="modal-stocks-hint">El símbolo no se puede modificar.</span>
					</div>
					<div className="modal-stocks-field">
						<label>Nombre</label>
						<input
							type="text"
							className="filter-input"
							value={ editForm.name }
							onChange={ (e) => this.onEditFieldChange('name', e.target.value) }
							placeholder="Nombre"
							disabled={ editSubmitting }
						/>
					</div>
					<div className="modal-stocks-field">
						<label>Priority</label>
						<input
							type="text"
							inputMode="numeric"
							className="filter-input"
							value={ editForm.priority }
							onChange={ (e) => this.onEditFieldChange('priority', e.target.value) }
							placeholder="0"
							disabled={ editSubmitting }
						/>
						<span className="modal-stocks-hint">Entero ≥ 0. Menor valor = mayor prioridad.</span>
					</div>

					<div className="modal-actions">
						<button
							className="cron-btn cron-btn-ghost"
							onClick={ this.closeEditModal }
							disabled={ editSubmitting }>
							Cancelar
						</button>
						<button
							className="cron-btn cron-btn-primary"
							onClick={ this.submitEdit }
							disabled={ editSubmitting }>
							{ editSubmitting ? 'Guardando…' : 'Guardar' }
						</button>
					</div>
				</div>
			</Modal>
		);
	}

	// ---------- Modal: usuarios ----------

	renderUsersModal() {
		const {
			usersModalOpen, usersModalStock, usersModalLoading, usersModalError, usersModalData,
		} = this.state;
		if (!usersModalOpen || !usersModalStock) return null;

		const count = Number(usersModalData?.count) || 0;
		const users = Array.isArray(usersModalData?.users) ? usersModalData!.users as CatalogUserRow[] : [];

		return (
			<Modal
				className="modal-stocks-users"
				visible={ usersModalOpen }
				title={ `Usuarios de ${ usersModalStock.symbol || 'activo' }` }
				onClose={ this.closeUsersModal }>
				<div className="modal-stocks-body">
					{ usersModalLoading && (
						<div className="stocks-empty">Cargando usuarios…</div>
					) }
					{ !usersModalLoading && usersModalError && (
						<div className="cron-error-card">
							No se pudo cargar la lista de usuarios.
						</div>
					) }
					{ !usersModalLoading && !usersModalError && count === 0 && (
						<div className="stocks-empty">Ningún usuario la usa.</div>
					) }
					{ !usersModalLoading && !usersModalError && count > 0 && (
						<>
							<div className="modal-stocks-users-summary">
								<strong>{ count }</strong> usuario{ count === 1 ? '' : 's' } usa{ count === 1 ? '' : 'n' } este activo.
							</div>
							<ul className="modal-stocks-users-list">
								{ users.map((u, i) => (
									<li className="modal-stocks-users-row" key={ String(u.id ?? `${ u.email }-${ i }`) }>
										<span className="modal-stocks-users-name">{ buildFullName(u) }</span>
										{ u.email && <span className="modal-stocks-users-email mono">{ u.email }</span> }
									</li>
								)) }
							</ul>
						</>
					) }

					<div className="modal-actions">
						<button
							className="cron-btn cron-btn-ghost"
							onClick={ this.closeUsersModal }>
							Cerrar
						</button>
					</div>
				</div>
			</Modal>
		);
	}

	render() {
		return (
			<div className="system-page stocks-page">
				{ this.renderHeader() }
				{ this.renderAssetTabs() }
				{ this.renderSearchSection() }
				{ this.renderStaleSection() }
				{ this.renderCatalogSection() }
				{ this.renderEditModal() }
				{ this.renderUsersModal() }
			</div>
		);
	}
}

export default Stocks;
